import { useState, useEffect, useCallback } from 'react';
import { getUserStats, formatMonthForAPI } from '../api';
import { useDemoMode } from '../providers/DemoModeProvider';

// Demo data for when demo mode is enabled
const mockUserStats = {
  totalPosts: 247,
  scheduledPosts: 18,
  publishedPosts: 229,
  engagementRate: 8.4,
  impressions: 124750,
  platformBreakdown: {
    twitter: { posts: 89, engagement: 9.2 },
    instagram: { posts: 76, engagement: 8.8 },
    linkedin: { posts: 52, engagement: 7.1 },
    facebook: { posts: 30, engagement: 6.9 }
  },
  topPerformingPosts: [
    { id: 1, content: "AI automation tips that boosted our productivity 🚀", engagement: 14.2, platform: "twitter" },
    { id: 2, content: "Behind the scenes: Building our new mobile app", engagement: 12.8, platform: "instagram" },
    { id: 3, content: "5 lessons learned from scaling our SaaS", engagement: 11.6, platform: "linkedin" }
  ],
  monthlyGrowth: {
    followers: 12.3,
    engagement: 8.7,
    reach: 15.2
  }
};

/**
 * Custom hook for managing user statistics
 * @param {string} initialMonth - Initial month in YYYY-MM format
 * @returns {Object} Hook state and functions
 */
export const useUserStats = (initialMonth = null) => {
  const { isDemoMode } = useDemoMode();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(
    initialMonth || formatMonthForAPI()
  );

  const fetchStats = useCallback(async (month = currentMonth) => {
    if (!month) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (isDemoMode) {
        // Use demo data when demo mode is enabled
        setTimeout(() => {
          setStats(mockUserStats);
          setLoading(false);
        }, 300); // Simulate network delay
      } else {
        const response = await getUserStats(month);
        setStats(response.stats);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching user stats:', err);
      setError(err.message || 'Failed to fetch user statistics');
      setLoading(false);
    }
  }, [currentMonth, isDemoMode]);

  // Fetch stats when month changes
  useEffect(() => {
    fetchStats(currentMonth);
  }, [currentMonth, fetchStats]);

  const changeMonth = useCallback((newMonth) => {
    if (newMonth !== currentMonth) {
      setCurrentMonth(newMonth);
    }
  }, [currentMonth]);

  const navigateMonth = useCallback((direction) => {
    const date = new Date(currentMonth + '-01');
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else if (direction === 'next') {
      date.setMonth(date.getMonth() + 1);
    }
    const newMonth = formatMonthForAPI(date);
    setCurrentMonth(newMonth);
  }, [currentMonth]);

  const refreshStats = useCallback(() => {
    fetchStats(currentMonth);
  }, [fetchStats, currentMonth]);

  return {
    stats,
    loading,
    error,
    currentMonth,
    changeMonth,
    navigateMonth,
    refreshStats,
    retry: refreshStats
  };
}; 